/**
 * DraggableModal
 *
 * Thin wrapper that adds drag-by-header behaviour to a modal box.
 *
 * Usage: replace the inner modal div (not the overlay) with this component.
 * The component applies a CSS `translate` transform to offset from the
 * initial CSS-centered position.  Position resets to center automatically
 * when the component unmounts (the parent's isOpen guard causes unmounting).
 *
 * Props:
 *   className  — classes applied to the wrapper div (e.g. "ai-modal ais-modal")
 *   children   — modal content; must contain an element with class .ai-modal-header
 *   onClose    — called on ESC keypress
 *   onClick    — forwarded to wrapper div (typically e.stopPropagation())
 *   style      — optional extra inline styles merged with the transform
 */

import { useRef, useState, useEffect } from 'react'

export default function DraggableModal({ className = '', children, onClose, onClick, style, wrapperStyle = {} }) {
  const [offset, setOffset] = useState({ x: 0, y: 0 })

  // Use refs for drag state — avoids triggering re-renders on every mousemove
  const dragging  = useRef(false)
  const dragStart = useRef({ mx: 0, my: 0, ox: 0, oy: 0 })

  // Stable mousemove / mouseup listeners (added once, use refs for mutable state)
  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return
      setOffset({
        x: dragStart.current.ox + (e.clientX - dragStart.current.mx),
        y: dragStart.current.oy + (e.clientY - dragStart.current.my),
      })
    }
    const onUp = () => { dragging.current = false }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup',   onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup',   onUp)
    }
  }, []) // intentionally empty — refs hold mutable state

  // ESC to close
  useEffect(() => {
    if (!onClose) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const handleMouseDown = (e) => {
    // Only start drag when clicking the modal header
    if (!e.target.closest('.ai-modal-header')) return
    dragging.current = true
    dragStart.current = { mx: e.clientX, my: e.clientY, ox: offset.x, oy: offset.y }
    e.preventDefault()
  }

  return (
    <div
      className={className}
      style={{
        ...style,
        ...wrapperStyle,
        transform: `translate(${offset.x}px, ${offset.y}px)`,
        userSelect: dragging.current ? 'none' : undefined,
      }}
      onMouseDown={handleMouseDown}
      onClick={onClick}
    >
      {children}
    </div>
  )
}
